import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertsPanel = ({ alerts, onDismissAlert, onConfigureAlert }) => {
  const [filter, setFilter] = useState('all');
  const [expandedAlert, setExpandedAlert] = useState(null);

  const filterOptions = [
    { value: 'all', label: 'All Alerts', count: alerts?.length },
    { value: 'critical', label: 'Critical', count: alerts?.filter(a => a?.severity === 'critical')?.length },
    { value: 'warning', label: 'Warning', count: alerts?.filter(a => a?.severity === 'warning')?.length },
    { value: 'info', label: 'Info', count: alerts?.filter(a => a?.severity === 'info')?.length }
  ];

  const filteredAlerts = filter === 'all' 
    ? alerts 
    : alerts?.filter(alert => alert?.severity === filter);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'limit_breach': return 'AlertTriangle';
      case 'concentration': return 'Target';
      case 'volatility': return 'Activity';
      case 'correlation': return 'GitBranch';
      case 'liquidity': return 'Droplets';
      case 'var_exceeded': return 'TrendingDown';
      default: return 'Bell';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-error/10 text-error border-error/20';
      case 'warning':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'info':
        return 'bg-accent/10 text-accent border-accent/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - alertTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Risk Alerts & Notifications</h3>
        <Button 
          variant="outline" 
          size="sm" 
          iconName="Settings" 
          iconSize={14}
          onClick={() => onConfigureAlert && onConfigureAlert()}
        >
          Configure
        </Button>
      </div>
      <div className="flex items-center space-x-2 mb-4">
        {filterOptions?.map((option) => (
          <button
            key={option?.value}
            onClick={() => setFilter(option?.value)}
            className={`px-3 py-1 rounded-full text-sm transition-smooth ${
              filter === option?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
          >
            {option?.label} ({option?.count})
          </button>
        ))}
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredAlerts?.length > 0 ? (
          filteredAlerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`border rounded-lg p-4 transition-smooth ${getSeverityColor(alert?.severity)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <Icon name={getAlertIcon(alert?.type)} size={20} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-foreground">{alert?.title}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(alert?.severity)}`}>
                        {alert?.severity}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{alert?.message}</p>
                    
                    {alert?.details && (
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div>Asset: <span className="font-medium">{alert?.details?.asset}</span></div>
                        <div>Current Value: <span className="font-medium">{alert?.details?.currentValue}</span></div>
                        <div>Threshold: <span className="font-medium">{alert?.details?.threshold}</span></div>
                      </div>
                    )}

                    {expandedAlert === alert?.id && alert?.recommendations && (
                      <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                        <h5 className="text-xs font-semibold text-foreground mb-2">Recommended Actions:</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          {alert?.recommendations?.map((rec, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <Icon name="ArrowRight" size={12} className="mt-0.5" />
                              <span>{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-muted-foreground">
                        {getTimeAgo(alert?.timestamp)}
                      </span>
                      <div className="flex items-center space-x-2">
                        {alert?.recommendations && (
                          <button
                            onClick={() => setExpandedAlert(
                              expandedAlert === alert?.id ? null : alert?.id
                            )}
                            className="text-xs text-accent hover:text-accent/80 transition-smooth"
                          >
                            {expandedAlert === alert?.id ? 'Hide' : 'Show'} Actions
                          </button>
                        )}
                        <button
                          onClick={() => onDismissAlert && onDismissAlert(alert?.id)}
                          className="text-xs text-muted-foreground hover:text-foreground transition-smooth"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} className="mx-auto text-success mb-3" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Active Alerts</h4>
            <p className="text-sm text-muted-foreground">
              All risk parameters are within acceptable limits
            </p>
          </div>
        )}
      </div>
      {filteredAlerts?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {filteredAlerts?.length} alerts • Last check: {new Date()?.toLocaleString('en-IN')}
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                iconName="RefreshCw" 
                iconSize={14}
              >
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                iconName="Download" 
                iconSize={14}
              >
                Export
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;